<template>
  <!-- Error Message -->
  <PageError v-if="error"
   :error="error"
   :errorCode="errorCode" 
  />

  <div v-else-if="canViewBlackbox || user?.is_staff" class="p-6 dark:text-white">
    <!-- Header Section -->
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
        Endpoint Availability - {{ filteredBlackboxes.length }}
        <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
      </h1>

      <div class="mb-4 text-base flex space-x-3 justify-between">
        <div class="relative group">
        <button
          type="button"
          class="hover:bg-transparent bg-transparent"
          @click="startTutorial"
        >
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
        </button>
          <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 mb-1 px-2 py-0.5 glass-card opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
        </div>
        <button v-if="canAddBlackbox || user?.is_staff"
          id="add-blackbox-btn"
          @click="goToAddBlackbox"
          class="btn bg-blue-700 text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Add Endpoint Availability
        </button>
      </div>
      
    </div>

    <!-- Organization Tabs -->
    <OrganizationTabs
      v-if="user?.is_staff || isServiceProviderUser"
      :organizations="tenant.organizations.value"
      :selected-org-id="tenant.currentOrganizationId.value"
      :loading="tenant.isLoading.value"
      :show-tabs="true"
      loading-text="Loading Endpoint Availability..."
      @select="handleOrgSelect"
    />

    <!-- Search and Filter Section -->
    <div
      class="flex flex-col md:flex-row md:items-center md:justify-between mb-6"
    >
      <!-- Search Bar -->
      <div class="mb-4 md:mb-0 md:flex-1 md:mr-4">
        <SearchBar
          v-model="searchQuery"
          placeholderText="Search endpoints by job name..."
        />
      </div>

      <!-- Bulk Action Section -->
      <div class="flex items-center mt-4 md:mt-0 space-x-2">
        <GlassSelect
          v-model="bulkAction"
          :options="canDeleteBlackbox || user?.is_staff ? [{ value: 'delete', label: 'Delete selected items' }] : []"
          placeholder="Bulk Actions"
          :valueKey="'value'"
          :labelKey="'label'"
        />
        <button
          @click="applyBulkAction"
          :disabled="!bulkAction || selectedBlackboxes.length === 0"
          :class="[
            'btn focus:outline-none focus:ring-2 focus:ring-blue-500',
            bulkAction && selectedBlackboxes.length
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed',
          ]"
        >
          Apply
        </button>
      </div>
    </div>

    <!-- Blackboxes Table -->
    <GenericTable
      id="blackboxes-table"
      ref="table"
      :columns="columns"
      :items="paginatedBlackboxes"
      itemKey="id"
      :selectable="true"
      @update:selectedItems="updateSelectedBlackboxes"
      @rowClick="goToBlackboxDetail"
    >
      <!-- Custom cell templates -->
      <template #created_date="{ value }">
        {{ formatDateTime(value) }}
      </template>
    </GenericTable>

    <!-- Pagination -->
    <div
      class="flex flex-col md:flex-row items-center justify-between mt-8 space-y-4 md:space-y-0"
    >
      <div class="flex items-center space-x-2">
        <button
          @click="changePage(-1)"
          :disabled="currentPage === 1"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 dark:text-white hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Previous
        </button>
        <button
          @click="changePage(1)"
          :disabled="currentPage === totalPages"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 dark:text-white hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Next
        </button>
      </div>
      <div class="text-gray-700 dark:text-white">
        Page {{ currentPage }} of {{ totalPages }}
      </div>
      <div>
        <GlassSelect
          v-model="itemsPerPage"
          :options="[{ value: 10, label: '10 per page' }, { value: 20, label: '20 per page' }, { value: 50, label: '50 per page' }, { value: 100, label: '100 per page' }, { value: -1, label: 'All' }]"
          :valueKey="'value'"
          :labelKey="'label'"
          placeholder="Items per page"
          @change="updatePagination"
        />
      </div>
    </div>

    <DeleteModal
      v-if="deletingItem"
      :deleting-item="deletingItem"
      :componet-objects="selectedObjects"
      :is-deleting="isDeleting"
      @delete="confirmDelete"
      @close="deletingItem=false"
      :isChildObject="true"
      :count="count"
    />

  </div>
</template>

<script>
import { getAllBlackboxes, deleteBlackbox } from "@/services/blackboxService";
import SearchBar from "@/components/SearchBar.vue";
import GenericTable from "@/components/GenericTable.vue";
import PageError from "@/components/PageError.vue";
import { formatError } from "@/utils/errorMessages";
import { useStore } from "@/store";
import DeleteModal from "@/components/DeleteModal.vue";
import { useTutorial } from "@/composables/tutorial";
import { useMultiTenant } from "@/composables/useMultiTenant";
import OrganizationTabs from "@/components/OrganizationTabs.vue";
import GlassSelect from "@/components/GlassSelect.vue";

export default {
  name: "BlackboxView",
  components: {
    SearchBar,
    GenericTable,
    PageError,
    OrganizationTabs,
    DeleteModal,
    GlassSelect,
  },
  data() {
    return {
      blackboxes: [],
      searchQuery: "",
      filteredBlackboxes: [],
      currentPage: 1,
      itemsPerPage: 10,
      selectedBlackboxes: [],
      deletingItem: false,
      bulkAction: "",
      columns: [
        { key: "job_name", label: "Job Name", sortable: true, width: "25%" },
        { key: "module", label: "Module", sortable: true, width: "20%" },
        {
          key: "created_date",
          label: "Created Date",
          sortable: true,
          width: "10%",
        },
      ],
      canAddBlackbox: true,
      canDeleteBlackbox: true,
      canViewBlackbox: true,
      error: "",
      isDeleting: false,
      blackboxTutorialSteps: [
        {
          element: '#searchbar',
          action: 'display',
          message: 'Search endpoint availability checks by job name.'
        },
        {
          element: '#add-blackbox-btn',
          action: 'display',
          message: 'Click here to create a new endpoint availability check.'
        },
        {
          element: '#blackboxes-table',
          action: 'display',
          message: 'Click any row to view or edit endpoint availability details.'
        },
      ],
    };
  },
  setup() {
    const tenant = useMultiTenant();
    const { start } = useTutorial();
    return { tenant, _start: start };
  },
  computed: {
    user(){
      return useStore.getUser();
    },
    isServiceProviderUser(){
      return useStore.isServiceProviderUser();
    },
    isFiltered() {
      return this.searchQuery !== "";
    },
    totalPages() {
      return this.itemsPerPage === -1
        ? 1
        : Math.ceil(this.filteredBlackboxes.length / this.itemsPerPage);
    },
    paginatedBlackboxes() {
      if (this.itemsPerPage === -1) {
        return this.filteredBlackboxes;
      }
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredBlackboxes.slice(start, end);
    },
    selectedObjects(){
        return this.selectedBlackboxes.map(blackbox => ({
          name: "Endpoint Availability",
          value: blackbox.job_name,
       }));
    },
    count(){
      return [{name: "Endpoint Availabilities", value: this.selectedObjects.length}]
    }
  },
  watch: {
    searchQuery() {
      this.applyFilters();
    },
    user: {
      handler(user) {
        if (user) {
          this.checkPermissions();
        }
      },
      immediate: true
    },
  },
  async mounted() {
    this.checkPermissions();
    await this.tenant.fetchOrganizations();
    if (this.tenant.currentOrganizationId.value) {
        await this.tenant.syncUrlParam();
        await this.fetchBlackboxes(this.tenant.currentSchemaName.value);
    } else {
      await this.tenant.loadInitialData(this.fetchBlackboxes);
    }
  },
  methods: {
    async startTutorial() {
      await this._start(this.blackboxTutorialSteps);
    },
    async handleOrgSelect(orgId) {
      try {
        this.error = "";
        await this.tenant.switchOrganization(orgId, this.fetchBlackboxes);
      } catch (error) {
        this.error = "Failed to switch organization";
        console.error('Organization select error:', error);
      }
    },
    async checkPermissions(){
      if (this.user) {
        this.canAddBlackbox = await useStore.hasPermission("Can add Blackbox");
        this.canDeleteBlackbox = await useStore.hasPermission("Can delete Blackbox");
        this.canViewBlackbox = await useStore.hasPermission("Can view Blackbox");
      }
    },
    async fetchBlackboxes(schemaName=null) {
      try {
        const blackboxes = await getAllBlackboxes(schemaName)
        this.blackboxes = [...blackboxes];
        this.applyFilters();
      } catch (error) {
        console.error("Error fetching blackboxes:", error);
        const { code, message } = formatError(error);
        this.errorCode = code;
        this.error = message;
      }
    },
    goToBlackboxDetail(blackbox) {
      this.$router.push({
        name: "BlackboxDetails",
        params: { id: blackbox.id },
        query: this.tenant.addSchemaParam(),
      });
    },
    goToAddBlackbox() {
      this.$router.push({ 
        name: "AddBlackbox",
        query: this.tenant.addSchemaParam(),
      });
    },
    updateSelectedBlackboxes(selectedItems) {
      this.selectedBlackboxes = selectedItems;
    },
    async applyBulkAction() {
      if (this.bulkAction === "delete" ) {
        this.deletingItem = true;
      }
      this.bulkAction = "";
    },
    async confirmDelete(schemaName=null) {
      this.isDeleting = true;
      await this.deleteSelectedBlackboxes(schemaName);
      this.deletingItem = false; 
      this.bulkAction = "";
    },
    async deleteSelectedBlackboxes() {
      if (this.selectedBlackboxes.length === 0) {
        alert("No blackboxes selected for deletion");
        return;
      }
      const validation = this.tenant.validateSchemaForOperation();
      if (!validation.valid) {
        this.error = "Malformed request. Failed to delete blackboxes!";
        this.errorCode = "422!";
        return;
      }
      try {
        const deletePromises = this.selectedBlackboxes.map((blackbox) =>
          deleteBlackbox(blackbox.id, validation.schemaName)
        );
        await Promise.all(deletePromises);
        
        this.fetchBlackboxes(validation.schemaName)
        this.$refs.table.clearSelection();
        this.applyFilters();
        this.selectedBlackboxes = [];
 
      } catch (error) {
        console.error("Error during bulk delete:", error);
        alert("An error occurred while deleting blackboxes. Please try again.");
      }  finally {
        this.isDeleting = false;
      }
    },
    formatDateTime(dateString) {
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    },
    async deleteBlackboxItem(id, schemaName=null) {
      if (confirm("Are you sure you want to delete this blackbox?")) {
        try {
          await deleteBlackbox(id, schemaName);
          this.fetchBlackboxes(schemaName)
          this.applyFilters(); // Re-apply filters to update the view
        } catch (error) {
          console.error("Error deleting blackbox:", error);
          // Handle error (e.g., show error message to user)
          this.error = "Failed to delete blackbox";
        }
      }
    },
    applyFilters() {
      const query = (this.searchQuery || '').toLowerCase();
      this.filteredBlackboxes = (this.blackboxes || []).filter((blackbox) => {
        const jobName = (blackbox?.job_name || '').toLowerCase();
        return jobName.includes(query);
      });
      this.currentPage = 1;
    },
    changePage(delta) {
      this.currentPage += delta;
    },
    updatePagination() {
      this.currentPage = 1;
    },
    updateSelectedBlackboxes(selectedItems) {
      this.selectedBlackboxes = selectedItems.filter((item => !item.marked_for_deletion));
    },
    formatDateTime(dateString) {
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    },
  },
};
</script>
